/* Five report pages — Make Day Summary, Milk Intake Usage,
 * Supplier Lot Usage, Backward Trace Report, Forward Trace Report.
 * Single file to keep things compact; each report has its own export
 * and is wired to its own route in App.js.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { getMakeDays, getMilkIntake, getSuppliers, getInwardDeliveries, backwardTrace, forwardTrace } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Search, Loader2 } from 'lucide-react';
import ReportLayout, { exportCsv } from './ReportLayout';

// ── 1) Make Day Summary Report ─────────────────────────────────────────
export function MakeDaySummaryReport() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => { (async () => {
    try { const r = await getMakeDays(); setItems(r.data); }
    catch (e) { toast.error('Load failed'); }
    finally { setLoading(false); }
  })(); }, []);

  const filtered = useMemo(() => items.filter(d => {
    if (dateFrom && d.make_date < dateFrom) return false;
    if (dateTo && d.make_date > dateTo) return false;
    return true;
  }), [items, dateFrom, dateTo]);

  const totals = useMemo(() => filtered.reduce((acc, d) => {
    const milk = (d.milk_used || []).reduce((s, m) => s + (m.quantity_litres || 0), 0);
    const batches = (d.output_batches || []).length;
    return { milk: acc.milk + milk, batches: acc.batches + batches, ing: acc.ing + (d.ingredients_used || []).length, pkg: acc.pkg + (d.packaging_used || []).length };
  }, { milk: 0, batches: 0, ing: 0, pkg: 0 }), [filtered]);

  const csv = () => exportCsv('make-day-summary.csv',
    filtered.map(d => ({
      ref: d.make_day_ref,
      date: d.make_date,
      product: d.product_type || '',
      supervisor: d.supervisor || '',
      milk_L: (d.milk_used || []).reduce((s, m) => s + (m.quantity_litres || 0), 0),
      ingredients: (d.ingredients_used || []).length,
      packaging: (d.packaging_used || []).length,
      batches: (d.output_batches || []).map(b => b.batch_number).join('; '),
      status: d.status,
    })),
    [
      { key: 'ref', label: 'Make Day' }, { key: 'date', label: 'Date' },
      { key: 'product', label: 'Product' }, { key: 'supervisor', label: 'Supervisor' },
      { key: 'milk_L', label: 'Milk (L)' }, { key: 'ingredients', label: 'Ingredient lines' },
      { key: 'packaging', label: 'Packaging lines' }, { key: 'batches', label: 'Output batches' },
      { key: 'status', label: 'Status' },
    ]);

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="report-make-day-summary">
      <div className="print:hidden flex items-center gap-3">
        <div><Label className="text-xs">From</Label><Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-[160px]" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-[160px]" /></div>
      </div>
      <ReportLayout title="Make Day Summary Report"
        subtitle={`${filtered.length} make days · ${totals.milk.toLocaleString()}L milk · ${totals.batches} output batches`}
        generated_at={new Date().toISOString()} onCsv={csv} testid="rpt-makeday-summary-layout">
        <table className="w-full text-sm border rounded">
          <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
            <tr><th className="text-left px-3 py-2">Ref</th><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Product</th><th className="text-left px-3 py-2">Supervisor</th><th className="text-right px-3 py-2">Milk (L)</th><th className="text-right px-3 py-2">Ing</th><th className="text-right px-3 py-2">Pkg</th><th className="text-right px-3 py-2">Batches</th><th className="text-left px-3 py-2">Status</th></tr>
          </thead>
          <tbody>{filtered.map((d, i) => {
            const milkL = (d.milk_used || []).reduce((s, m) => s + (m.quantity_litres || 0), 0);
            return (<tr key={i} className="border-t">
              <td className="px-3 py-2 font-mono text-xs">{d.make_day_ref}</td>
              <td className="px-3 py-2">{d.make_date}</td>
              <td className="px-3 py-2">{d.product_type || '—'}</td>
              <td className="px-3 py-2">{d.supervisor || '—'}</td>
              <td className="px-3 py-2 text-right font-medium">{milkL.toLocaleString()}</td>
              <td className="px-3 py-2 text-right">{(d.ingredients_used || []).length}</td>
              <td className="px-3 py-2 text-right">{(d.packaging_used || []).length}</td>
              <td className="px-3 py-2 text-right">{(d.output_batches || []).length}</td>
              <td className="px-3 py-2"><Badge variant="outline" className="text-xs">{d.status}</Badge></td>
            </tr>);
          })}</tbody>
        </table>
      </ReportLayout>
    </div>
  );
}

// ── 2) Milk Intake Usage Report ────────────────────────────────────────
export function MilkIntakeUsageReport() {
  const [intakes, setIntakes] = useState([]);
  const [makeDays, setMakeDays] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { (async () => {
    try {
      const [i, m, s] = await Promise.all([getMilkIntake(), getMakeDays(), getSuppliers()]);
      setIntakes(i.data); setMakeDays(m.data); setSuppliers(s.data);
    } catch (e) { toast.error('Load failed'); }
    finally { setLoading(false); }
  })(); }, []);

  const supName = (id) => suppliers.find(s => s.id === id)?.name || '—';

  const rows = useMemo(() => intakes.map(ix => {
    const used = makeDays.flatMap(md => (md.milk_used || []).filter(mu => mu.milk_intake_id === ix.id).map(mu => ({ make_day_ref: md.make_day_ref, make_date: md.make_date, quantity_litres: mu.quantity_litres })));
    const usedL = used.reduce((s, u) => s + (u.quantity_litres || 0), 0);
    return { ...ix, used_lines: used, used_L: usedL, remaining_L: (ix.volume_litres || 0) - usedL };
  }), [intakes, makeDays]);

  const csv = () => exportCsv('milk-intake-usage.csv',
    rows.map(r => ({
      intake_ref: r.intake_ref, date: r.intake_date, supplier: supName(r.supplier_id),
      volume_L: r.volume_litres, used_L: r.used_L, remaining_L: r.remaining_L,
      antibiotic: r.antibiotic_result, used_on: r.used_lines.map(u => `${u.make_day_ref}(${u.quantity_litres}L)`).join('; '),
    })),
    [{key:'intake_ref',label:'Intake'},{key:'date',label:'Date'},{key:'supplier',label:'Supplier'},{key:'volume_L',label:'Volume L'},{key:'used_L',label:'Used L'},{key:'remaining_L',label:'Remaining L'},{key:'antibiotic',label:'Antibiotic'},{key:'used_on',label:'Used on'}]);

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <ReportLayout title="Milk Intake Usage Report"
      subtitle={`${rows.length} intakes · ${rows.reduce((s, r) => s + r.volume_litres, 0).toLocaleString()}L received · ${rows.reduce((s, r) => s + r.used_L, 0).toLocaleString()}L used`}
      generated_at={new Date().toISOString()} onCsv={csv} testid="rpt-milk-intake-usage">
      <table className="w-full text-sm border rounded">
        <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
          <tr><th className="text-left px-3 py-2">Intake</th><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Supplier</th><th className="text-right px-3 py-2">Received (L)</th><th className="text-right px-3 py-2">Used (L)</th><th className="text-right px-3 py-2">Remaining</th><th className="text-left px-3 py-2">Used on</th></tr>
        </thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i} className="border-t">
            <td className="px-3 py-2 font-mono text-xs">{r.intake_ref}</td>
            <td className="px-3 py-2">{r.intake_date}</td>
            <td className="px-3 py-2">{supName(r.supplier_id)}</td>
            <td className="px-3 py-2 text-right">{r.volume_litres?.toLocaleString()}</td>
            <td className="px-3 py-2 text-right font-medium">{r.used_L.toLocaleString()}</td>
            <td className="px-3 py-2 text-right text-muted-foreground">{r.remaining_L.toLocaleString()}</td>
            <td className="px-3 py-2 text-xs">{r.used_lines.length === 0 ? <span className="text-muted-foreground italic">unused</span> : r.used_lines.map(u => <span key={u.make_day_ref} className="font-mono mr-2">{u.make_day_ref} ({u.quantity_litres}L)</span>)}</td>
          </tr>))}</tbody>
      </table>
    </ReportLayout>
  );
}

// ── 3) Supplier Lot Usage Report ───────────────────────────────────────
export function SupplierLotUsageReport() {
  const [deliveries, setDeliveries] = useState([]);
  const [makeDays, setMakeDays] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { (async () => {
    try {
      const [d, m, s] = await Promise.all([getInwardDeliveries(), getMakeDays(), getSuppliers()]);
      setDeliveries(d.data); setMakeDays(m.data); setSuppliers(s.data);
    } catch (e) { toast.error('Load failed'); }
    finally { setLoading(false); }
  })(); }, []);

  const supName = (id) => suppliers.find(s => s.id === id)?.name || '—';

  // Flatten all delivery lines into a "lot" list, then for each lot find usage on make-days
  const lots = useMemo(() => {
    const out = [];
    for (const d of deliveries) {
      for (const ln of (d.lines || [])) {
        if (!ln.supplier_lot) continue;
        const usage = makeDays.flatMap(md => [
          ...(md.ingredients_used || []).filter(u => u.supplier_lot === ln.supplier_lot).map(u => ({ make_day_ref: md.make_day_ref, qty: u.quantity, unit: u.unit })),
          ...(md.packaging_used || []).filter(u => u.supplier_lot === ln.supplier_lot).map(u => ({ make_day_ref: md.make_day_ref, qty: u.quantity, unit: u.unit })),
        ]);
        const usedQty = usage.reduce((s, u) => s + (Number(u.qty) || 0), 0);
        out.push({
          supplier: supName(d.supplier_id),
          supplier_id: d.supplier_id,
          delivery_ref: d.reference,
          delivery_date: d.delivery_date,
          internal_item: ln.internal_item,
          supplier_lot: ln.supplier_lot,
          received_qty: ln.quantity,
          unit: ln.unit,
          used_qty: usedQty,
          used_on: usage,
        });
      }
    }
    return out;
  }, [deliveries, makeDays, suppliers]);

  const csv = () => exportCsv('supplier-lot-usage.csv',
    lots.map(l => ({ ...l, used_on: l.used_on.map(u => `${u.make_day_ref}(${u.qty}${u.unit})`).join('; ') })),
    [{key:'supplier',label:'Supplier'},{key:'delivery_ref',label:'Delivery'},{key:'delivery_date',label:'Delivery date'},{key:'internal_item',label:'Item'},{key:'supplier_lot',label:'Lot'},{key:'received_qty',label:'Received'},{key:'unit',label:'Unit'},{key:'used_qty',label:'Used'},{key:'used_on',label:'Used on'}]);

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <ReportLayout title="Supplier Lot Usage Report"
      subtitle={`${lots.length} traceable supplier lots`}
      generated_at={new Date().toISOString()} onCsv={csv} testid="rpt-supplier-lot-usage">
      <table className="w-full text-sm border rounded">
        <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
          <tr><th className="text-left px-3 py-2">Supplier</th><th className="text-left px-3 py-2">Item</th><th className="text-left px-3 py-2">Lot</th><th className="text-left px-3 py-2">Delivery</th><th className="text-right px-3 py-2">Received</th><th className="text-right px-3 py-2">Used</th><th className="text-left px-3 py-2">Used on</th></tr>
        </thead>
        <tbody>{lots.map((l, i) => (
          <tr key={i} className="border-t">
            <td className="px-3 py-2">{l.supplier}</td>
            <td className="px-3 py-2">{l.internal_item}</td>
            <td className="px-3 py-2 font-mono text-xs">{l.supplier_lot}</td>
            <td className="px-3 py-2 font-mono text-xs">{l.delivery_ref} <span className="text-muted-foreground">· {l.delivery_date}</span></td>
            <td className="px-3 py-2 text-right">{l.received_qty} {l.unit}</td>
            <td className="px-3 py-2 text-right font-medium">{l.used_qty} {l.unit}</td>
            <td className="px-3 py-2 text-xs">{l.used_on.length === 0 ? <span className="text-muted-foreground italic">unused</span> : l.used_on.map(u => <span key={u.make_day_ref + u.qty} className="font-mono mr-2">{u.make_day_ref}</span>)}</td>
          </tr>))}</tbody>
      </table>
    </ReportLayout>
  );
}

// ── 4 + 5) Backward / Forward trace reports ────────────────────────────
const TRACE_TYPES = [
  { value: 'cheese_batch_number', label: 'Cheese batch', placeholder: 'CB-2026-014' },
  { value: 'sales_order_number', label: 'Sales order', placeholder: 'SO-2026-058' },
  { value: 'dispatch_ref', label: 'Dispatch', placeholder: 'DSP-001' },
  { value: 'make_day_ref', label: 'Make Day', placeholder: 'MD-2026-00001' },
  { value: 'milk_intake_ref', label: 'Milk intake', placeholder: 'MI-2026-00001' },
  { value: 'supplier_lot', label: 'Supplier lot', placeholder: 'MA4001-26A007' },
];

function TraceReportShared({ direction, title }) {
  const [type, setType] = useState('cheese_batch_number');
  const [val, setVal] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!val.trim()) { toast.error('Enter a value'); return; }
    setLoading(true);
    try {
      const fn = direction === 'backward' ? backwardTrace : forwardTrace;
      const r = await fn({ [type]: val.trim() });
      setData(r.data);
    } catch (e) { toast.error('Trace failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4" data-testid={`rpt-${direction}-trace`}>
      <div className="print:hidden">
        <h1 className="text-xl font-semibold">{title}</h1>
        <p className="text-sm text-muted-foreground">Printable {direction} traceability report. Run a trace, then click Print or Export CSV.</p>
      </div>
      <Card className="print:hidden"><CardContent className="pt-4">
        <div className="flex flex-wrap gap-2 items-end">
          <div><Label className="text-xs">Start from</Label>
            <Select value={type} onValueChange={setType}><SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
              <SelectContent>{TRACE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Value</Label><Input value={val} onChange={e => setVal(e.target.value)} placeholder={TRACE_TYPES.find(t => t.value === type)?.placeholder} className="w-[260px]" onKeyDown={e => e.key === 'Enter' && run()} /></div>
          <Button onClick={run} disabled={loading} data-testid={`btn-run-${direction}-rpt`}>{loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Search className="w-4 h-4 mr-1" />}Run</Button>
        </div>
      </CardContent></Card>

      {data && <ReportLayout title={title} subtitle={data.starting} generated_at={new Date().toISOString()} testid={`rpt-${direction}-layout`}>
        <pre className="text-xs bg-zinc-50 p-3 rounded border overflow-x-auto">{JSON.stringify(data, null, 2)}</pre>
        <div className="mt-2 text-xs text-muted-foreground italic print:hidden">Tip: the visual timeline is on /traceability/{direction}. This report page is intended for the print-friendly export.</div>
      </ReportLayout>}
    </div>
  );
}

export function BackwardTraceReport() { return <TraceReportShared direction="backward" title="Backward Trace Report" />; }
export function ForwardTraceReport() { return <TraceReportShared direction="forward" title="Forward Trace Report" />; }
